import { useApiUsers } from "@/queries/settings/use-api-users.query.ts";
import { ApiApiUserFilter } from "@/types/api-user.types.ts";
import {
  Button,
  LinkButtonTo,
  ListItem,
  Modal,
  ModalProps,
  Multiselect,
  SelectItem,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ServiceUsersSelectModalProps extends ModalProps {
  selectedEntities: SelectItem[];
  onApply: (entities: SelectItem[]) => void;
}

export function ServiceUsersSelectModal({
  selectedEntities,
  onApply,
  ...props
}: ServiceUsersSelectModalProps) {
  const { id: modalId } = props;
  const { close } = useModals();
  const { t } = useTranslation();

  const [selected, setSelected] = useState<SelectItem[]>(selectedEntities);
  const [filter, setFilter] = useState<ApiApiUserFilter>({ page_number: 1 });
  const { data: apiUsers, isLoading } = useApiUsers(filter);

  const searchOptions = useMemo<SelectItem[]>(
    () => (apiUsers?.items ?? []).map((user) => ({ id: user.id, title: user.name })),
    [apiUsers]
  );

  const handleSearch = (text: string | undefined) => {
    setFilter((prev) => ({ ...prev, page_number: 1, text: text ?? "" }));
  };

  const apply = () => {
    onApply(selected);
    close(modalId);
  };

  const handleDelete = () => {
    onApply([]);
    close(modalId);
  };

  return (
    <Modal
      {...props}
      title={t("core:service_users")}
      withCloser
      actions={
        <Stack gap="sm">
          <Button size={"lg"} onClick={apply}>
            {t("common:save")}
          </Button>
          <Button size={"md"} variant="plain" onClick={() => close(modalId)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <Multiselect
          isMulti
          searchable
          label={t("core:service_users")}
          emptyTitle={t("common:select")}
          isLoading={isLoading}
          options={selectedEntities}
          searchOptions={searchOptions}
          onSearchOptions={handleSearch}
          loadOptionsOnOpen
          values={selected.map((entity) => entity.id)}
          onChange={(_, entities) => setSelected(entities)}
        />

        {selectedEntities.length > 0 && (
          <Stack>
            <hr className={"border-dark/10"} />
            <ListItem
              title={t("core:delete_rule_question")}
              valueSlot={
                <LinkButtonTo onClick={handleDelete} variant={"danger"}>
                  {t("common:delete")}
                </LinkButtonTo>
              }
            />
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
